import { Button, SegmentGroup, Tag } from "@ttc3k/trekker";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { NoteCard, EmptyState } from "shared/components";
import { Flex, Box } from "styled-system/jsx";
import { NotesContentProps } from "./NotesContent";
import { useNotesContext } from "shared/contextProviders";
import { ArrowLeft, ArrowRight } from "iconoir-react";
import { css } from "styled-system/css";

enum NoteType {
  Open = "OPEN",
  Archived = "ARCHIVED",
}

export type SegmentedNotesViewProps = Pick<
  NotesContentProps,
  "isCreatingNote" | "setIsCreatingNote"
>;

export const SegmentedNotesView = ({
  isCreatingNote,
  setIsCreatingNote,
}: SegmentedNotesViewProps) => {
  const [selectedNoteType, setSelectedNoteType] = useState<NoteType>(
    NoteType.Open,
  );
  const [selectedNoteIndex, setSelectedNoteIndex] = useState(0);

  const { t } = useTranslation();
  const { notes, viewOnly } = useNotesContext();

  const archivedNotes = useMemo(
    () => notes.filter(note => note.isArchived),
    [notes],
  );
  const currentNotes = useMemo(
    () =>
      selectedNoteType === NoteType.Open
        ? notes.filter(note => !note.isArchived)
        : notes.filter(note => note.isArchived),
    [notes, selectedNoteType],
  );

  const noteTypes = useMemo(
    () => [
      {
        label: t("core:NOTES.TYPES.OPEN"),
        value: NoteType.Open,
      },
      {
        label: t("core:NOTES.TYPES.ARCHIVED"),
        value: NoteType.Archived,
      },
    ],
    [t],
  );

  return (
    <Flex flexDirection={"column"} gap={"300"}>
      <SegmentGroup.Root
        segmentStyle="buttons"
        orientation="horizontal"
        value={selectedNoteType}
        onValueChange={value => {
          setSelectedNoteType(value.value as NoteType);
          setSelectedNoteIndex(0);
        }}
      >
        {noteTypes.map(noteType => (
          <SegmentGroup.Item
            key={noteType.value}
            value={noteType.value}
            disabled={
              noteType.value === NoteType.Archived &&
              archivedNotes &&
              archivedNotes.length === 0
            }
          >
            {noteType.label}
          </SegmentGroup.Item>
        ))}
      </SegmentGroup.Root>

      <Box position="relative" minH="256px" overflow="hidden">
        {!isCreatingNote &&
          selectedNoteType === NoteType.Open &&
          currentNotes.length === 0 && (
            <EmptyState
              text={t("core:NOTES.EMPTY")}
              ctaButtonProps={
                !viewOnly && {
                  children: t("core:NOTES.CREATE"),
                  size: "md",
                  onClick: () => setIsCreatingNote(true),
                }
              }
              p={viewOnly ? "400" : "600"}
              minH="256px"
            />
          )}
        {isCreatingNote && (
          <NoteCard
            initialMode="edit"
            onCancel={() => setIsCreatingNote(false)}
            onSaveSuccess={() => setIsCreatingNote(false)}
            className={css({ minH: "256px" })}
            size="lg"
          />
        )}
        {currentNotes.map((note, index) => (
          <NoteCard
            initialMode="view"
            note={note}
            key={note._id}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              width: "100%",
              height: "100%",
              transform: `translateX(${(index - selectedNoteIndex + (isCreatingNote && index >= selectedNoteIndex ? 1 : 0)) * 100}%)`,
              transition: "all 0.2s ease-in-out",
            }}
            size="lg"
          />
        ))}
      </Box>

      {currentNotes.length > 0 && (
        <Flex flexDir="row" justifyContent="space-between" alignItems="center">
          <Tag visual="primary" mode="light">
            {selectedNoteIndex + 1}/{currentNotes.length}
          </Tag>
          <Flex flexDir="row" gap="100">
            <Button
              visual="primary"
              mode="light"
              size="sm"
              onClick={() =>
                setSelectedNoteIndex(prev => Math.max(prev - 1, 0))
              }
              disabled={isCreatingNote || selectedNoteIndex === 0}
              Icon={ArrowLeft}
            />
            <Button
              visual="primary"
              mode="light"
              size="sm"
              onClick={() =>
                setSelectedNoteIndex(prev =>
                  Math.min(prev + 1, currentNotes.length - 1),
                )
              }
              disabled={
                isCreatingNote || selectedNoteIndex === currentNotes.length - 1
              }
              Icon={ArrowRight}
            />
          </Flex>
        </Flex>
      )}
    </Flex>
  );
};
